import {
  formatDay,
  shiftDay,
  warsawToday,
  type CalorieWeightDto,
} from '@iac/contracts';
import { useMemo, useState, type FormEvent } from 'react';
import { DataTable, Td, Tr } from '../../components/DataTable';
import { DeleteButton } from '../../components/DeleteButton';
import { Dialog } from '../../components/Dialog';
import { EmptyState } from '../../components/EmptyState';
import { Field } from '../../components/Field';
import { IconPencilSquare } from '../../components/icons';
import { LineChart } from '../../components/LineChart';
import { PageHeader } from '../../components/PageHeader';
import { Panel } from '../../components/Panel';
import { SegmentedControl } from '../../components/SegmentedControl';
import { Stat, StatGrid } from '../../components/Stat';
import { useDict } from '../../i18n/dictionary';
import { useLocale } from '../../lib/locale';
import { usePageTitle } from '../../lib/page';
import {
  useCalorieSettings,
  useDeleteWeight,
  useSaveWeight,
  useUpdateWeight,
  useWeights,
} from './api';
import { caloriesDict } from './i18n';
import { formatWeight } from './links';

const RANGES = ['30', '90', '365', 'all'] as const;
type Range = (typeof RANGES)[number];
const DEFAULT_RANGE: Range = '90';
const RANGE_KEY = 'caloriesProgressRange';

/** The range is a per-browser convenience; storage may be unavailable. */
function readRange(): Range {
  try {
    const value = localStorage.getItem(RANGE_KEY);
    return RANGES.includes(value as Range) ? (value as Range) : DEFAULT_RANGE;
  } catch {
    return DEFAULT_RANGE;
  }
}

function writeRange(range: Range) {
  try {
    localStorage.setItem(RANGE_KEY, range);
  } catch {
    // Not remembering the choice is fine.
  }
}

/** The first day an N-day range covers — today counts as one — or none for all. */
function rangeStart(range: Range, today: string): string | null {
  return range === 'all' ? null : shiftDay(today, -(Number(range) - 1));
}

/** "−1,8 kg", "+0,4 kg", "0,0 kg". Rounded first, so −0.04 is not "−0,0". */
function signedKg(kg: number): string {
  const rounded = Math.round(kg * 10) / 10;
  const sign = rounded > 0 ? '+' : rounded < 0 ? '−' : '';
  return `${sign}${formatWeight(Math.abs(rounded))} kg`;
}

type WeightValues = { date: string; weightKg: string };

/** Adding and editing alike; `weight` seeds the fields once. */
function WeightForm({
  idPrefix,
  weight,
  today,
  submitLabel,
  pending,
  onSubmit,
  onCancel,
}: {
  idPrefix: string;
  weight?: CalorieWeightDto;
  today: string;
  submitLabel: string;
  pending: boolean;
  onSubmit: (values: WeightValues, reset: () => void) => void;
  onCancel?: () => void;
}) {
  const t = useDict(caloriesDict);
  const [date, setDate] = useState(weight?.date ?? today);
  const [weightKg, setWeightKg] = useState(
    weight ? formatWeight(weight.weightKg) : '',
  );

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    onSubmit({ date, weightKg }, () => setWeightKg(''));
  }

  return (
    <form onSubmit={submit} className="form-stack">
      <div className="progress-form-fields">
        <Field
          id={`${idPrefix}-date`}
          label={t.date}
          type="date"
          required
          max={today}
          value={date}
          onChange={(e) => setDate(e.target.value)}
        />
        <Field
          id={`${idPrefix}-weight`}
          label={t.weightKg}
          type="text"
          inputMode="decimal"
          autoComplete="off"
          placeholder="81,2"
          required
          autoFocus={weight !== undefined}
          value={weightKg}
          onChange={(e) => setWeightKg(e.target.value)}
        />
      </div>
      <div className="form-actions">
        {onCancel && (
          <button type="button" className="btn-ghost" onClick={onCancel}>
            {t.cancel}
          </button>
        )}
        <button type="submit" className="btn-primary" disabled={pending}>
          {submitLabel}
        </button>
      </div>
    </form>
  );
}

function WeightRow({
  weight,
  onEdit,
}: {
  weight: CalorieWeightDto;
  onEdit: () => void;
}) {
  const t = useDict(caloriesDict);
  const { locale } = useLocale();
  const remove = useDeleteWeight();
  const day = formatDay(weight.date, locale);

  return (
    <Tr>
      <Td>{day}</Td>
      <Td numeric>{formatWeight(weight.weightKg)} kg</Td>
      <Td>
        <div className="row-actions">
          <button
            type="button"
            className="btn-icon-ghost"
            aria-label={t.editItem(day)}
            title={t.edit}
            onClick={onEdit}
          >
            <IconPencilSquare />
          </button>
          <DeleteButton
            label={t.deleteItem(day)}
            confirm={t.confirmDeleteWeight(day)}
            onConfirm={() => remove.mutate(weight.id)}
          />
        </div>
      </Td>
    </Tr>
  );
}

/**
 * Body weight over time: the chart and change follow the chosen range; the
 * current weight, the distance to the target and the table never do.
 */
export function CaloriesProgress() {
  const t = useDict(caloriesDict);
  const { locale } = useLocale();
  usePageTitle(`${t.progress} — ${t.titleSuffix}`);
  const { data: weights } = useWeights();
  const { data: settings } = useCalorieSettings();
  const save = useSaveWeight();
  const update = useUpdateWeight();
  const [range, setRange] = useState(readRange);
  const [editing, setEditing] = useState<CalorieWeightDto | null>(null);
  const close = () => setEditing(null);
  const today = warsawToday();

  const shortDay = useMemo(
    () =>
      new Intl.DateTimeFormat(locale, {
        day: 'numeric',
        month: 'numeric',
        timeZone: 'UTC',
      }),
    [locale],
  );

  const ranges: { value: Range; label: string }[] = [
    { value: '30', label: t.range30 },
    { value: '90', label: t.range90 },
    { value: '365', label: t.range365 },
    { value: 'all', label: t.rangeAll },
  ];
  const changeLabel = {
    '30': t.change30,
    '90': t.change90,
    '365': t.change365,
    all: t.changeAll,
  }[range];

  // Newest first, as the API sends them.
  const start = rangeStart(range, today);
  const inRange = (weights ?? []).filter(
    (w) => start === null || w.date >= start,
  );
  const current = weights?.[0];
  const target = settings?.targetWeightKg ?? null;

  return (
    <>
      <PageHeader title={t.progress} />

      {weights && weights.length > 0 && (
        <>
          <div className="progress-range">
            <SegmentedControl
              label={t.range}
              options={ranges}
              value={range}
              onChange={(next) => {
                setRange(next);
                writeRange(next);
              }}
            />
          </div>

          <Panel as="div" className="progress-chart">
            {inRange.length > 0 ? (
              <LineChart
                label={t.weightChart}
                points={[...inRange]
                  .reverse()
                  .map((w) => ({ date: w.date, value: w.weightKg }))}
                reference={
                  target === null
                    ? undefined
                    : { value: target, label: t.target }
                }
                axis="fit"
                formatDate={(date) =>
                  shortDay.format(new Date(`${date}T00:00:00Z`))
                }
                formatValue={formatWeight}
              />
            ) : (
              <p className="text-hint">{t.noWeightsInRange}</p>
            )}
          </Panel>

          <StatGrid>
            {current && (
              <Stat
                label={t.currentWeight}
                value={`${formatWeight(current.weightKg)} kg`}
              />
            )}
            <Stat
              label={changeLabel}
              value={
                inRange.length > 0
                  ? signedKg(
                      inRange[0].weightKg -
                        inRange[inRange.length - 1].weightKg,
                    )
                  : '—'
              }
            />
            {current && target !== null && (
              <Stat
                label={t.toTarget}
                value={signedKg(target - current.weightKg)}
              />
            )}
          </StatGrid>
        </>
      )}

      {weights && weights.length === 0 && (
        <div className="progress-empty">
          <EmptyState size="lg">{t.noWeights}</EmptyState>
        </div>
      )}

      <Panel title={t.addWeight}>
        <WeightForm
          idPrefix="weight-add"
          today={today}
          submitLabel={t.add}
          pending={save.isPending}
          onSubmit={(values, reset) =>
            save.mutate(values, { onSuccess: reset })
          }
        />
      </Panel>

      {weights && weights.length > 0 && (
        <>
          <h2 className="heading-section">{t.measurements}</h2>
          <DataTable
            columns={[
              { label: t.date },
              { label: t.bodyWeight, align: 'center' },
              { align: 'center' },
            ]}
            empty={t.noWeights}
          >
            {weights.map((weight) => (
              <WeightRow
                key={weight.id}
                weight={weight}
                onEdit={() => setEditing(weight)}
              />
            ))}
          </DataTable>
        </>
      )}

      <Dialog
        open={editing !== null}
        title={editing ? t.editWeight(formatDay(editing.date, locale)) : ''}
        onClose={close}
      >
        {editing && (
          <WeightForm
            key={editing.id}
            idPrefix="weight-edit"
            weight={editing}
            today={today}
            submitLabel={t.save}
            pending={update.isPending}
            onSubmit={(values) =>
              update.mutate({ id: editing.id, ...values }, { onSuccess: close })
            }
            onCancel={close}
          />
        )}
      </Dialog>
    </>
  );
}
